import React, { useEffect } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useDispatch } from 'react-redux'

import {
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
} from '@chakra-ui/react'

import { DatePicker, Select } from '@/components/Form'

import useUser from '@/features/users/useUser'

//Service
import { useCostCenter } from '@/features/costCenters/useCostCenter'
import { useDelegation } from '@/features/delegations/useDelegation'

//Constant
import MultiSelect from '@/components/Form/Select/MultiSelect'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { toast } from 'react-toastify'

const DelegationDetailModal = ({ modalDetail, setModalDetail }) => {
  const { show, isCreate, detail } = modalDetail

  // Hook
  const { useUserOptions } = useUser()
  const { useFetchAllCostCenters, useCostCenterOptions } = useCostCenter()
  const { useCreateDelegation, useUpdateDelegation } = useDelegation()

  const createDelegation = useCreateDelegation()
  const updateDelegation = useUpdateDelegation()
  const methods = useForm({ defaultValues: detail })
  const delegatorId = useWatch({ control: methods.control, name: 'delegator._id' })
  const dispatch = useDispatch()

  //Translated data
  const { data: delegatorOptions } = useUserOptions({ labelKey: 'fullName', valueKey: '_id' })
  const delegateeOptions = delegatorOptions.filter((option) => option.value !== delegatorId)

  const { data: costCenterOptions } = useCostCenterOptions({ labelType: 'detailed', valueKey: '_id' })
  const allCostCenterOptions = [{ value: 'all', label: 'All' }].concat(costCenterOptions)

  useEffect(() => {
    methods.reset(detail)
  }, [detail, methods])

  const closeModal = () => {
    setModalDetail({
      show: false,
      isCreate: true,
      detail: {},
    })
  }
  const onSubmit = async (data) => {
    let costCenters = data.delegatedCostCenter
    if (data.delegatedCostCenter.includes('all')) {
      costCenters = costCenterOptions?.map((costCenter) => costCenter.value)
    }
    const payload = {
      ...data,
      delegatedCostCenter: costCenters,
      delegator: data.delegatorId,
      delegatee: data.delegateeId,
    }

    dispatch(showLoadingOverlay())
    try {
      if (isCreate) {
        await createDelegation.mutateAsync(payload)
      } else {
        const id = detail._id

        if (!id) {
          return
        }

        await updateDelegation.mutateAsync({ id, data: payload })
      }
      closeModal()
    } catch (err) {
      toast.error(err.message)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  return (
    <Modal isOpen={show} onClose={closeModal}>
      <ModalOverlay />
      <ModalContent maxW={'40%'}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ModalHeader>
              <Heading>{isCreate ? 'Add' : 'Update'} Delegations</Heading>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack>
                <Select
                  name="delegatorId"
                  label="Delegator"
                  placeholder="Enter value"
                  options={delegatorOptions}
                  isRow
                />
                <Select
                  name="delegateeId"
                  label="Delegatee"
                  placeholder="Enter value"
                  options={delegateeOptions}
                  isRow
                />

                <MultiSelect
                  name="delegatedCostCenter"
                  label="Delegated Cost Center"
                  placeholder="Enter value"
                  options={allCostCenterOptions}
                  isRow
                />
                <DatePicker name="validStartDate" label="Start Date (DD-MMM-YYYY)" isRow needPortal={false} />
                <DatePicker name="validEndDate" label="End Date (DD-MMM-YYYY)" isRow needPortal={false} />
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="green" mr={3} type="submit">
                {isCreate ? 'Add' : 'Update'}
              </Button>

              <Button colorScheme="gray" onClick={closeModal}>
                Close
              </Button>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  )
}

export default DelegationDetailModal
